/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `segment_point` message.
 *
 * Navigation and race evaluation point for a segment decribing a point along the segment path and time it took each segment leader to reach that point
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SegmentPointMesg : Mesg {
    public constructor() : super("SegmentPoint", Profile.MesgNum.SEGMENT_POINT)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** position_lat, in semicircles. */
    public var positionLat: Int?
        get() = getFieldValue(POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LAT_FIELD_NUM, value)

    /** position_long, in semicircles. */
    public var positionLong: Int?
        get() = getFieldValue(POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LONG_FIELD_NUM, value)

    /** distance, in m. Accumulated distance along the segment at the described point */
    public var distance: Double?
        get() = getFieldValue(DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(DISTANCE_FIELD_NUM, value)

    /** altitude, in m. Accumulated altitude along the segment at the described point */
    public var altitude: Double?
        get() = getFieldValue(ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ALTITUDE_FIELD_NUM, value)

    /** Number of values stored in leader_time. */
    public val numLeaderTime: Int
        get() = getField(LEADER_TIME_FIELD_NUM)?.numValues ?: 0

    /** leader_time, in s. Accumualted time each leader board member required to reach the described point. This value is zero for all leader board members at the starting point of the segment. */
    public var leaderTime: List<Double?>?
        get() {
            val field = getField(LEADER_TIME_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeaderTime(it) }
        }
        set(value) {
            removeField(LEADER_TIME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeaderTime(index, element) }
        }

    public fun getLeaderTime(index: Int): Double? =
        getFieldValue(LEADER_TIME_FIELD_NUM, index) as? Double

    public fun setLeaderTime(index: Int, value: Double?) {
        setFieldValue(LEADER_TIME_FIELD_NUM, value, index)
    }

    /** enhanced_altitude, in m. Accumulated altitude along the segment at the described point */
    public var enhancedAltitude: Double?
        get() = getFieldValue(ENHANCED_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_ALTITUDE_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val POSITION_LAT_FIELD_NUM: UByte = 1u
        public const val POSITION_LONG_FIELD_NUM: UByte = 2u
        public const val DISTANCE_FIELD_NUM: UByte = 3u
        public const val ALTITUDE_FIELD_NUM: UByte = 4u
        public const val LEADER_TIME_FIELD_NUM: UByte = 5u
        public const val ENHANCED_ALTITUDE_FIELD_NUM: UByte = 6u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[1] = Field("PositionLat", 1u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[2] = Field("PositionLong", 2u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[3] = Field("Distance", 3u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[4] = Field("Altitude", 4u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(6u, false, 16, 5.0, 500.0))
            fields[5] = Field("LeaderTime", 5u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[6] = Field("EnhancedAltitude", 6u, BaseType.UINT32, 5.0, 500.0, "m", false)
            return fields
        }
    }
}
